import { canScore } from './evaluationIntegrity';
// #Fase0-B1 — Autoguardado del checklist web: solo lo que el usuario tocó.
//
// Antes buildSavePayload mandaba TODOS los items cada 2.5 s con
// `score: item.score ?? 0` y `selected_options ?? []`. La API marca
// score_set=True con cualquier score, así que un solo guardado desde la web:
//   - dejaba todos los reactivos "calificados con 0" (sin contestar = 0),
//   - hacía que /submit ya no encontrara obligatorios pendientes,
//   - y borraba las opciones que se habían elegido desde el móvil.
//
// Ahora se lleva un registro de campos "sucios" por item con una revisión
// (contador creciente). Al guardar se toma una foto del registro, se manda
// SOLO eso, y al terminar se limpia únicamente lo que no volvió a cambiar
// mientras el PUT iba en vuelo; lo editado durante el guardado sigue sucio
// y sale en el siguiente.
//
// Son funciones puras para poder probarlas sin montar la pantalla.

// Campos que acepta EvalItemUpdate en PUT /evaluations/{id}/items.
// has_been_scored / score_set son banderas locales: la API marca
// score_set=True sola cuando recibe un score.
export const SAVE_FIELDS = [
  'provider_ids',
  'location',
  'zone',
  'not_applicable',
  'na_reason',
  'recommendation',
  'score',
  'notes',
  'answer_text',
  'answer_numeric',
  'answer_date',
  'answer_bool',
  'selected_options',
];
const SAVE_FIELD_SET = new Set(SAVE_FIELDS);

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const INT_RE = /^-?\d+$/;

/**
 * Registra en `dirty` (mutándolo) que los campos de `partial` del item
 * `key` cambiaron en la revisión `rev`. Ignora campos que no se guardan.
 * Devuelve true si marcó algo.
 */
export function markDirty(dirty, key, partial, rev) {
  const fields = Object.keys(partial || {}).filter((f) => SAVE_FIELD_SET.has(f));
  if (fields.length === 0) return false;
  const entry = dirty[key] || {};
  fields.forEach((f) => { entry[f] = rev; });
  dirty[key] = entry;
  return true;
}

/** Copia profunda (un nivel) del registro de sucios para usarla como foto. */
export function snapshotDirty(dirty) {
  const snap = {};
  Object.entries(dirty).forEach(([key, fields]) => { snap[key] = { ...fields }; });
  return snap;
}

/**
 * Tras un guardado exitoso, quita de `dirty` (mutándolo) cada campo cuya
 * revisión siga siendo la de la foto. Si el usuario lo volvió a editar
 * mientras el guardado iba en vuelo, la revisión cambió y se queda sucio.
 */
export function clearSaved(dirty, snapshot) {
  Object.entries(snapshot).forEach(([key, fields]) => {
    const current = dirty[key];
    if (!current) return;
    Object.entries(fields).forEach(([f, rev]) => {
      if (current[f] === rev) delete current[f];
    });
    if (Object.keys(current).length === 0) delete dirty[key];
  });
}

export function hasDirtyItems(dirty) {
  return Object.keys(dirty).length > 0;
}

/**
 * Identidad del item para la API. En la pantalla los items se indexan por
 * criterio_id (legacy o plantilla migrada, entero) o por question_id (UUID,
 * plantillas nuevas). EvalItemUpdate espera criterio_id entero y question_id
 * texto; en plantillas la API empareja por question_id.
 */
export function itemIdentity(key, item = {}) {
  const keyStr = String(key);
  const question_id = item.question_id || (UUID_RE.test(keyStr) ? keyStr : null);
  const rawCid = item.criterio_id ?? key;
  let criterio_id = null;
  if (typeof rawCid === 'number' && Number.isInteger(rawCid)) criterio_id = rawCid;
  else if (typeof rawCid === 'string' && INT_RE.test(rawCid.trim())) criterio_id = Number(rawCid.trim());
  if (question_id) return { criterio_id, question_id };
  if (criterio_id !== null) return { criterio_id, question_id: null };
  // Ni entero ni UUID: se manda tal cual; la API lo toma como question_id
  // en evaluaciones con plantilla (#API-1).
  return { criterio_id: keyStr, question_id: null };
}

/**
 * Arma el item del PUT con la identidad y SOLO los campos indicados.
 * - notes vacías viajan como "" (así se pueden borrar; null = "sin cambio").
 * - selected_options siempre es arreglo (deseleccionar todo = []).
 */
export function buildItemPayload(key, item = {}, fields = []) {
  const out = itemIdentity(key, item);
  fields.forEach((f) => {
    if (!SAVE_FIELD_SET.has(f)) return;
    if (f === 'notes') out[f] = item[f] ?? '';
    else if (f === 'recommendation') out[f] = item[f] === undefined ? '' : item[f];
    else if (
      item.not_applicable &&
      [
        'score',
        'answer_text',
        'answer_numeric',
        'answer_date',
        'answer_bool',
        'selected_options',
      ].includes(f)
    )
      return;
    else if (f === 'selected_options') out.selected_options = Array.isArray(item.selected_options) ? item.selected_options : [];
    else out[f] = item[f] ?? null;
  });
  return out;
}

/** Arma la lista de items del PUT a partir de una foto del registro. */
export function buildItemsPayload(snapshot, localItems) {
  return Object.entries(snapshot).map(([key, fields]) =>
    buildItemPayload(key, localItems[key] || {}, Object.keys(fields)));
}

// ─── "Contestado" (#Fase0-B2) ────────────────────────────────────────────────

/** true si el reactivo de escala ya se calificó (el 0 por defecto no cuenta). */
export function isScored(item) {
  return !!item && (item.score_set === true || item.has_been_scored === true) && typeof item.score === 'number';
}

/**
 * Un reactivo está contestado si tiene alguna respuesta propia de su tipo.
 * #Fase0-B2 — ya NO se usa score > 0 como señal: los items nacen con
 * score 0, y un score solo cuenta si viene marcado (score_set / has_been_scored).
 */
export function isQuestionAnswered(item) {
  if (!item) return false;
  if (item.has_been_scored === true || item.score_set === true) return true;
  if (item.answer_text && String(item.answer_text).trim()) return true;
  if (item.answer_numeric !== null && item.answer_numeric !== undefined) return true;
  if (item.answer_date) return true;
  if (item.answer_bool !== null && item.answer_bool !== undefined) return true;
  if (Array.isArray(item.selected_options) && item.selected_options.length > 0) return true;
  if (Array.isArray(item.evidence_urls) && item.evidence_urls.length > 0) return true;
  return false;
}

/**
 * #Fase1-1 — Tipos calificables según el contrato; no calcula totales.
 */
export function isScorableCriterion(criterion) {
  return !!criterion && canScore(criterion);
}

// #Fase1-2 — Un id descartado por la API no debe reintentarse aunque cambió en vuelo.
export function clearSkipped(dirty, skipped = [], localItems = {}) {
  const ids = new Set(skipped.map(String));
  Object.keys(dirty).forEach((key) => {
    const item = localItems[key] || {};
    if ([key, item.question_id, item.criterio_id].some((id) => id != null && ids.has(String(id))))
      delete dirty[key];
  });
}
